import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Trash2,
  GripVertical,
  Save,
  Eye,
  ChevronDown,
  ChevronUp,
  Settings,
  FileText,
  HelpCircle,
  ExternalLink,
  Copy,
  Loader2,
  CheckCircle,
  AlertTriangle,
  ToggleLeft,
  ToggleRight,
  Hash,
  Type,
  List,
  Camera,
  Link as LinkIcon
} from 'lucide-react';
import { api } from '@/utils/api';

interface Question {
  id: string;
  text: string;
  type: 'yesno' | 'text' | 'select' | 'number' | 'date';
  options?: string[];
  points: number;
  photoRequired: boolean;
  conditional?: string;
  regulation?: {
    code: string;
    section?: string;
    description: string;
    link?: string;
  };
}

interface Element {
  id: string;
  name: string;
  description?: string;
  conditional?: string;
  questions: Question[];
  expanded: boolean;
}

interface Template {
  id?: number;
  name: string;
  description: string;
  program: string;
  version: string;
  elements: Element[];
  metadata: {
    passThreshold: number;
    conditionalPassThreshold: number;
    requiresPhotos: boolean;
    auditType: string;
  };
}

const QUESTION_TYPES = [
  { id: 'yesno', name: 'Yes/No/N/A', icon: ToggleLeft },
  { id: 'text', name: 'Text Input', icon: Type },
  { id: 'select', name: 'Multiple Choice', icon: List },
  { id: 'number', name: 'Number', icon: Hash },
  { id: 'date', name: 'Date', icon: FileText },
];

const AUDIT_TYPES = [
  'Health and Safety Inspection',
  'Quality Audit',
  'Construction Supervisor Checklist',
  'Equipment Inspection',
  'Documentation Review',
  'Contractor Compliance',
  'Environmental Audit',
  'Custom',
];

const COMMON_REGULATIONS = [
  { code: 'OSHA 29 CFR 1926.20', description: 'Safety Programs and Provisions' },
  { code: 'OSHA 29 CFR 1926.21', description: 'Safety Training and Education' },
  { code: 'OSHA 29 CFR 1926.95', description: 'Personal Protective Equipment' },
  { code: 'OSHA 29 CFR 1926.501', description: 'Fall Protection Requirements' },
  { code: 'OSHA 29 CFR 1926.502', description: 'Fall Protection Systems' },
  { code: 'OSHA 29 CFR 1926.251', description: 'Rigging Equipment' },
  { code: 'ANSI Z359.1', description: 'Fall Arrest Systems' },
  { code: 'ANSI/ASSP A10.48', description: 'Telecommunications Tower Safety' },
  { code: 'ASME B30.9', description: 'Slings Safety Standard' },
  { code: 'Company Policy', description: 'Internal company policy' },
];

export function AuditTemplateBuilderPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [activeQuestion, setActiveQuestion] = useState<string | null>(null);

  const [template, setTemplate] = useState<Template>({
    name: '',
    description: '',
    program: 'Custom Audit',
    version: '1.0',
    elements: [],
    metadata: {
      passThreshold: 80,
      conditionalPassThreshold: 60,
      requiresPhotos: false,
      auditType: 'Custom',
    },
  });

  useEffect(() => {
    if (id) {
      loadTemplate(parseInt(id));
    }
  }, [id]);

  const loadTemplate = async (templateId: number) => {
    try {
      const response = await api.get(`/safety-audits/templates/${templateId}`);
      const data = response.data.data;
      setTemplate({
        ...data,
        elements: (data.elements || []).map((el: any) => ({ ...el, expanded: false })),
      });
    } catch (error) {
      console.error('Error loading template:', error);
    }
  };

  const generateId = () => `q_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const addElement = () => {
    const newElement: Element = {
      id: `element_${template.elements.length + 1}`,
      name: `Element ${template.elements.length + 1}`,
      description: '',
      questions: [],
      expanded: true,
    };
    setTemplate(prev => ({
      ...prev,
      elements: [...prev.elements, newElement],
    }));
  };

  const updateElement = (elementId: string, updates: Partial<Element>) => {
    setTemplate(prev => ({
      ...prev,
      elements: prev.elements.map(el =>
        el.id === elementId ? { ...el, ...updates } : el
      ),
    }));
  };

  const deleteElement = (elementId: string) => {
    if (!confirm('Delete this element and all its questions?')) return;
    setTemplate(prev => ({
      ...prev,
      elements: prev.elements.filter(el => el.id !== elementId),
    }));
  };

  const addQuestion = (elementId: string) => {
    const newQuestion: Question = {
      id: generateId(),
      text: '',
      type: 'yesno',
      points: 1,
      photoRequired: false,
    };
    setTemplate(prev => ({
      ...prev,
      elements: prev.elements.map(el =>
        el.id === elementId
          ? { ...el, questions: [...el.questions, newQuestion] }
          : el
      ),
    }));
    setActiveQuestion(newQuestion.id);
  };

  const updateQuestion = (elementId: string, questionId: string, updates: Partial<Question>) => {
    setTemplate(prev => ({
      ...prev,
      elements: prev.elements.map(el =>
        el.id === elementId
          ? {
              ...el,
              questions: el.questions.map(q =>
                q.id === questionId ? { ...q, ...updates } : q
              ),
            }
          : el
      ),
    }));
  };

  const deleteQuestion = (elementId: string, questionId: string) => {
    setTemplate(prev => ({
      ...prev,
      elements: prev.elements.map(el =>
        el.id === elementId
          ? { ...el, questions: el.questions.filter(q => q.id !== questionId) }
          : el
      ),
    }));
  };

  const duplicateQuestion = (elementId: string, question: Question) => {
    const newQuestion: Question = {
      ...question,
      id: generateId(),
      text: `${question.text} (copy)`,
    };
    setTemplate(prev => ({
      ...prev,
      elements: prev.elements.map(el =>
        el.id === elementId
          ? { ...el, questions: [...el.questions, newQuestion] }
          : el
      ),
    }));
  };

  const moveElement = (index: number, direction: 'up' | 'down') => {
    const newElements = [...template.elements];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newElements.length) return;
    [newElements[index], newElements[newIndex]] = [newElements[newIndex], newElements[index]];
    setTemplate(prev => ({ ...prev, elements: newElements }));
  };

  const moveQuestion = (elementId: string, questionIndex: number, direction: 'up' | 'down') => {
    setTemplate(prev => ({
      ...prev,
      elements: prev.elements.map(el => {
        if (el.id !== elementId) return el;
        const newQuestions = [...el.questions];
        const newIndex = direction === 'up' ? questionIndex - 1 : questionIndex + 1;
        if (newIndex < 0 || newIndex >= newQuestions.length) return el;
        [newQuestions[questionIndex], newQuestions[newIndex]] = [newQuestions[newIndex], newQuestions[questionIndex]];
        return { ...el, questions: newQuestions };
      }),
    }));
  };

  const handleSave = async () => {
    if (!template.name.trim()) {
      alert('Please enter a template name');
      return;
    }
    if (template.elements.length === 0) {
      alert('Please add at least one element');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...template,
        elements: template.elements.map(({ expanded, ...el }) => el),
      };

      if (isEditing) {
        await api.put(`/safety-audits/templates/${id}`, payload);
      } else {
        await api.post('/safety-audits/templates', payload);
      }

      navigate('/safety-audits/templates');
    } catch (error: any) {
      console.error('Error saving template:', error);
      alert(error.response?.data?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const getTotalQuestions = () => {
    return template.elements.reduce((sum, el) => sum + el.questions.length, 0);
  };

  const getTotalPoints = () => {
    return template.elements.reduce(
      (sum, el) => sum + el.questions.reduce((qSum, q) => qSum + (Number.isFinite(q.points) ? q.points : 0), 0),
      0
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700 sticky top-0 z-20">
        <div className="container mx-auto px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <Link to="/safety-audits/templates" className="text-slate-400 hover:text-white">
                <ArrowLeft className="h-6 w-6" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-white">
                  {isEditing ? 'Edit Template' : 'Create Audit Template'}
                </h1>
                <p className="text-sm text-slate-400">
                  {getTotalQuestions()} questions • {getTotalPoints()} total points
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition ${
                  showPreview
                    ? 'bg-purple-600 text-white'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                <Eye className="h-4 w-4" />
                Preview
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:from-emerald-600 hover:to-teal-700 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Template
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Editor */}
          <div className="lg:col-span-2 space-y-6">
            {/* Template Info */}
            <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Settings className="h-5 w-5 text-emerald-400" />
                Template Settings
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Template Name *</label>
                  <input
                    type="text"
                    value={template.name}
                    onChange={(e) => setTemplate(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="e.g., Quality Audit Checklist"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Audit Type</label>
                  <select
                    value={template.metadata.auditType}
                    onChange={(e) => setTemplate(prev => ({
                      ...prev,
                      program: e.target.value,
                      metadata: { ...prev.metadata, auditType: e.target.value },
                    }))}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  >
                    {AUDIT_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-300 mb-1">Description</label>
                  <textarea
                    value={template.description}
                    onChange={(e) => setTemplate(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    rows={2}
                    placeholder="Describe the purpose of this audit template..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Pass Threshold (%)</label>
                  <input
                    type="number"
                    value={template.metadata.passThreshold}
                    onChange={(e) => setTemplate(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, passThreshold: parseInt(e.target.value) || 80 },
                    }))}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    min={0}
                    max={100}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Conditional Pass (%)</label>
                  <input
                    type="number"
                    value={template.metadata.conditionalPassThreshold}
                    onChange={(e) => setTemplate(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, conditionalPassThreshold: parseInt(e.target.value) || 60 },
                    }))}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    min={0}
                    max={100}
                  />
                </div>
              </div>
            </div>

            {/* Elements */}
            <div className="space-y-4">
              {template.elements.map((element, elementIndex) => (
                <div
                  key={element.id}
                  className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden"
                >
                  {/* Element Header */}
                  <div className="p-4 bg-slate-700/50 flex items-center gap-4">
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => moveElement(elementIndex, 'up')}
                        disabled={elementIndex === 0}
                        className="text-slate-400 hover:text-white disabled:opacity-30"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => moveElement(elementIndex, 'down')}
                        disabled={elementIndex === template.elements.length - 1}
                        className="text-slate-400 hover:text-white disabled:opacity-30"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="flex-1 grid md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={element.name}
                        onChange={(e) => updateElement(element.id, { name: e.target.value })}
                        className="px-3 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white font-semibold"
                        placeholder="Element Name"
                      />
                      <input
                        type="text"
                        value={element.description || ''}
                        onChange={(e) => updateElement(element.id, { description: e.target.value })}
                        className="px-3 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white text-sm"
                        placeholder="Description (optional)"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-400">
                        {element.questions.length} Q
                      </span>
                      <button
                        onClick={() => updateElement(element.id, { expanded: !element.expanded })}
                        className="p-2 text-slate-400 hover:text-white"
                      >
                        {element.expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                      </button>
                      <button
                        onClick={() => deleteElement(element.id)}
                        className="p-2 text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  {/* Questions */}
                  {element.expanded && (
                    <div className="p-4 space-y-3">
                      {element.questions.map((question, qIndex) => (
                        <div
                          key={question.id}
                          className={`p-4 rounded-lg border transition ${
                            activeQuestion === question.id
                              ? 'bg-emerald-900/20 border-emerald-600'
                              : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex flex-col gap-1 mt-2">
                              <button
                                onClick={() => moveQuestion(element.id, qIndex, 'up')}
                                disabled={qIndex === 0}
                                className="text-slate-400 hover:text-white disabled:opacity-30"
                              >
                                <ChevronUp className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => moveQuestion(element.id, qIndex, 'down')}
                                disabled={qIndex === element.questions.length - 1}
                                className="text-slate-400 hover:text-white disabled:opacity-30"
                              >
                                <ChevronDown className="h-3 w-3" />
                              </button>
                            </div>
                            <div className="flex-1 space-y-3">
                              <div className="flex gap-3">
                                <input
                                  type="text"
                                  value={question.text}
                                  onChange={(e) => updateQuestion(element.id, question.id, { text: e.target.value })}
                                  onClick={() => setActiveQuestion(question.id)}
                                  className="flex-1 px-3 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white text-sm"
                                  placeholder="Question text..."
                                />
                                <select
                                  value={question.type}
                                  onChange={(e) => updateQuestion(element.id, question.id, { type: e.target.value as any })}
                                  className="px-3 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white text-sm"
                                >
                                  {QUESTION_TYPES.map(type => (
                                    <option key={type.id} value={type.id}>{type.name}</option>
                                  ))}
                                </select>
                              </div>

                              {/* Question Options (for expanded view) */}
                              {activeQuestion === question.id && (
                                <div className="grid md:grid-cols-3 gap-3 pt-2 border-t border-slate-600">
                                  <div>
                                    <label className="block text-xs text-slate-400 mb-1">Points</label>
                                    <input
                                      type="number"
                                      value={question.points}
                                      onChange={(e) => updateQuestion(element.id, question.id, { points: parseInt(e.target.value) || 1 })}
                                      className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                                      min={0}
                                    />
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      id={`photo_${question.id}`}
                                      checked={question.photoRequired}
                                      onChange={(e) => updateQuestion(element.id, question.id, { photoRequired: e.target.checked })}
                                      className="rounded"
                                    />
                                    <label htmlFor={`photo_${question.id}`} className="text-sm text-slate-300 flex items-center gap-1">
                                      <Camera className="h-4 w-4" />
                                      Photo Required
                                    </label>
                                  </div>
                                  <div>
                                    <label className="block text-xs text-slate-400 mb-1">Conditional</label>
                                    <input
                                      type="text"
                                      value={question.conditional || ''}
                                      onChange={(e) => updateQuestion(element.id, question.id, { conditional: e.target.value })}
                                      className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                                      placeholder="e.g., excavation"
                                    />
                                  </div>

                                  {/* Regulation Reference */}
                                  <div className="md:col-span-3">
                                    <label className="block text-xs text-slate-400 mb-1 flex items-center gap-1">
                                      <LinkIcon className="h-3 w-3" />
                                      Regulation/Standard Reference
                                    </label>
                                    <div className="grid md:grid-cols-3 gap-2">
                                      <select
                                        value={question.regulation?.code || ''}
                                        onChange={(e) => {
                                          const reg = COMMON_REGULATIONS.find(r => r.code === e.target.value);
                                          updateQuestion(element.id, question.id, {
                                            regulation: reg ? { code: reg.code, description: reg.description } : undefined,
                                          });
                                        }}
                                        className="px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                                      >
                                        <option value="">Select standard...</option>
                                        {COMMON_REGULATIONS.map(reg => (
                                          <option key={reg.code} value={reg.code}>{reg.code}</option>
                                        ))}
                                      </select>
                                      <input
                                        type="text"
                                        value={question.regulation?.description || ''}
                                        onChange={(e) => updateQuestion(element.id, question.id, {
                                          regulation: { ...question.regulation, code: question.regulation?.code || '', description: e.target.value },
                                        })}
                                        className="px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                                        placeholder="Description..."
                                      />
                                      <input
                                        type="url"
                                        value={question.regulation?.link || ''}
                                        onChange={(e) => updateQuestion(element.id, question.id, {
                                          regulation: { ...question.regulation, code: question.regulation?.code || '', description: question.regulation?.description || '', link: e.target.value },
                                        })}
                                        className="px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                                        placeholder="Link URL..."
                                      />
                                    </div>
                                  </div>

                                  {/* Select Options */}
                                  {question.type === 'select' && (
                                    <div className="md:col-span-3">
                                      <label className="block text-xs text-slate-400 mb-1">Options (comma separated)</label>
                                      <input
                                        type="text"
                                        value={question.options?.join(', ') || ''}
                                        onChange={(e) => updateQuestion(element.id, question.id, {
                                          options: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
                                        })}
                                        className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                                        placeholder="Option 1, Option 2, Option 3..."
                                      />
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => duplicateQuestion(element.id, question)}
                                className="p-1.5 text-slate-400 hover:text-blue-400"
                                title="Duplicate"
                              >
                                <Copy className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => deleteQuestion(element.id, question.id)}
                                className="p-1.5 text-slate-400 hover:text-red-400"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}

                      <button
                        onClick={() => addQuestion(element.id)}
                        className="w-full py-3 border-2 border-dashed border-slate-600 rounded-lg text-slate-400 hover:text-emerald-400 hover:border-emerald-600 transition flex items-center justify-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Add Question
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {/* Add Element Button */}
              <button
                onClick={addElement}
                className="w-full py-4 border-2 border-dashed border-slate-600 rounded-xl text-slate-400 hover:text-emerald-400 hover:border-emerald-600 transition flex items-center justify-center gap-2"
              >
                <Plus className="h-5 w-5" />
                Add Element/Section
              </button>
            </div>
          </div>

          {/* Sidebar - Preview */}
          <div className="lg:col-span-1">
            <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6 sticky top-24">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Eye className="h-5 w-5 text-purple-400" />
                Template Preview
              </h3>

              {template.elements.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No elements yet</p>
                  <p className="text-sm">Add elements to see preview</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                  {template.elements.map((element, i) => (
                    <div key={element.id} className="bg-slate-700/50 rounded-lg p-3">
                      <div className="font-semibold text-white text-sm mb-2">
                        {i + 1}. {element.name || 'Untitled Element'}
                      </div>
                      <div className="space-y-1">
                        {element.questions.slice(0, 3).map((q, qi) => (
                          <div key={q.id} className="text-xs text-slate-400 truncate">
                            {qi + 1}. {q.text || 'Untitled question'}
                          </div>
                        ))}
                        {element.questions.length > 3 && (
                          <div className="text-xs text-slate-500">
                            +{element.questions.length - 3} more questions
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6 pt-4 border-t border-slate-700">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="bg-slate-700/50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-white">{template.elements.length}</div>
                    <div className="text-xs text-slate-400">Elements</div>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-white">{getTotalQuestions()}</div>
                    <div className="text-xs text-slate-400">Questions</div>
                  </div>
                </div>
                <div className="mt-3 bg-emerald-900/30 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-emerald-400">{getTotalPoints()}</div>
                  <div className="text-xs text-slate-400">Total Points</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

